import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Select, { components } from 'react-select'
import moment from 'moment'
import { fetchData, toasterrormsg } from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import Loader from '../components/Loader'
import { Tr } from '../../../_metronic/i18n/Tr'
import { DynamicChart, TooltipField } from '../dashboard/DashboardChartDynamic'
import { selectStyles } from '../analytics/components/selectStyles'
import { Category } from '../types/adsSettings'

const TOOLTIP_FIELDS: TooltipField[] = [
  { key: 'revenue', label: 'Revenue', format: 'number' },
  { key: 'totalSponser', label: 'Total Sponsers', format: 'raw' },
]

const START_YEAR = 2020

// Value of the "All …" row in every filter — it stands for "send nothing for
// this param", which is what the page starts out with.
const ALL_VALUE = ''

type Option = { value: string; label: string }

// Keep the dropdown menus above the chart card (ApexCharts sets its own stacking context).
const filterSelectStyles = {
  ...selectStyles,
  menu: (base: any) => ({ ...base, zIndex: 1056 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 1056 }),
}

// How many category chips stay visible before the rest collapse into "+N more".
const MAX_VISIBLE_CATEGORIES = 2

// Selecting every category would otherwise wrap the chips over several rows and
// push the whole filter bar around, so the control keeps a single line: the
// extra chips collapse into a counter and the typed search never grows the box.
const categorySelectStyles = {
  ...filterSelectStyles,
  control: (base: any, state: any) => ({
    ...(filterSelectStyles as any).control?.(base, state) ?? base,
    flexWrap: 'nowrap',
  }),
  valueContainer: (base: any) => ({
    ...base,
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  input: (base: any) => ({ ...base, minWidth: 2 }),
}

const CategoryValueContainer = ({ children, ...props }: any) => {
  const [values, input] = children
  const selected = Array.isArray(values) ? values : []
  const hidden = selected.length - MAX_VISIBLE_CATEGORIES

  return (
    <components.ValueContainer {...props}>
      {hidden > 0 ? (
        <>
          {selected.slice(0, MAX_VISIBLE_CATEGORIES)}
          <span className='text-gray-600 fs-8 fw-semibold ms-1 text-nowrap'>+{hidden}</span>
        </>
      ) : (
        values
      )}
      {input}
    </components.ValueContainer>
  )
}

// The API may ignore a comma separated categoryId and answer with every category,
// so the selection is enforced here as well — by id when the row carries one,
// otherwise by the category name.
const keepSelectedCategories = (list: any[], selected: Option[]) => {
  if (!selected.length) return list

  const ids = new Set(selected.map((option) => option.value))
  const names = new Set(selected.map((option) => option.label.toLowerCase()))

  const filtered = list.filter((item: any) =>
    item?.categoryId != null
      ? ids.has(String(item.categoryId))
      : names.has(String(item?.name ?? '').toLowerCase())
  )

  // Nothing matched — the rows carry neither a known id nor a known name, so
  // fall back to what the API sent instead of showing an empty chart.
  return filtered.length ? filtered : list
}

const RevenueByCategory: FC = () => {
  const t = Tr()

  const [isLoading, setIsLoading] = useState(false)
  const [chartData, setChartData] = useState<{ name: string; data: Record<string, any>[] }[]>([])
  const [chartCategories, setChartCategories] = useState<string[]>([])
  const [totalRevenue, setTotalRevenue] = useState('0')
  const [chartLength, setChartLength] = useState('0')

  // ── Filters ──
  // Every filter is staged in a draft while the user picks and only reaches the
  // API on Apply, so a three field change is one request instead of three.
  const [draftYear, setDraftYear] = useState<Option | null>(null)
  const [draftMonth, setDraftMonth] = useState<Option | null>(null)
  const [draftCategories, setDraftCategories] = useState<Option[]>([])
  const [selectedYear, setSelectedYear] = useState<Option | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<Option | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<Option[]>([])
  const [categoryOptions, setCategoryOptions] = useState<Option[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(true)

  // Each list opens with an "All …" row so one filter can be reset back to its
  // unfiltered state — an empty param, exactly like the first load — without
  // having to clear the other two.
  const yearOptions = useMemo<Option[]>(() => {
    const currentYear = moment().year()
    return [
      { value: ALL_VALUE, label: t('All Years') },
      ...Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) => ({
        value: String(currentYear - i),
        label: String(currentYear - i),
      })),
    ]
  }, [t])

  const monthOptions = useMemo<Option[]>(
    () => [
      { value: ALL_VALUE, label: t('All Months') },
      ...moment.months().map((name, index) => ({
        value: String(index + 1),
        label: t(name),
      })),
    ],
    [t]
  )

  const categorySelectOptions = useMemo<Option[]>(
    () => [{ value: ALL_VALUE, label: t('All Categories') }, ...categoryOptions],
    [categoryOptions, t]
  )

  const hasActiveFilter = Boolean(selectedYear || selectedMonth || selectedCategories.length)
  // Clear has to reach a staged but not yet applied selection too.
  const canClear = hasActiveFilter || Boolean(draftYear || draftMonth || draftCategories.length)

  // Apply only lights up while the staged filters differ from the applied ones.
  const hasPendingFilters = useMemo(() => {
    const ids = (options: Option[]) =>
      options
        .map((option) => option.value)
        .sort()
        .join(',')
    return (
      draftYear?.value !== selectedYear?.value ||
      draftMonth?.value !== selectedMonth?.value ||
      ids(draftCategories) !== ids(selectedCategories)
    )
  }, [draftYear, draftMonth, draftCategories, selectedYear, selectedMonth, selectedCategories])

  const emptyMessage = hasActiveFilter
    ? t('No revenue found for the selected filters — try a different year, month or category')
    : t('No Category revenue data available')

  // ── Category list — dynamic (same source as the rest of the admin) ──
  const fetchCategoryOptions = useCallback(async () => {
    try {
      setCategoriesLoading(true)
      const response = await fetchData('admin/categorylist', {})
      if (response?.status == '200') {
        const list = response?.data ?? []
        const options = (Array.isArray(list) ? list : [])
          .filter((cat: Category) => cat?.categoryId != null)
          .map((cat: Category) => ({
            value: String(cat.categoryId),
            label: cat.en_name || `#${cat.categoryId}`,
          }))
        setCategoryOptions(options)
      }
    } catch (error) {
      console.error('Error fetching category list:', error)
    } finally {
      setCategoriesLoading(false)
    }
  }, [])

  const fetchCategoryRevenue = useCallback(async () => {
    try {
      setIsLoading(true)
      const param = {
        year: selectedYear?.value ?? '',
        month: selectedMonth?.value ?? '',
        categoryId: selectedCategories.map((c) => c.value).join(','),
      }
      const response = await fetchData('admin/sponser/categorywise/revenue', param)
      if (response.status == '200') {
        const list = response.data?.list || response.data || []
        buildChart(keepSelectedCategories(Array.isArray(list) ? list : [], selectedCategories))
      } else {
        toasterrormsg(response?.message || 'An error occurred')
        resetChart()
      }
    } catch (error) {
      console.error('Error fetching category revenue:', error)
      toasterrormsg('Failed to fetch category revenue')
      resetChart()
    } finally {
      setIsLoading(false)
    }
  }, [selectedYear, selectedMonth, selectedCategories])

  const buildChart = (list: any[]) => {
    if (!list || list.length === 0) {
      resetChart()
      return
    }

    const categories = list.map((item: any) =>  item.name || 'Unknown')
    const dataPoints = list.map((item: any) => ({
      x:  item.name || 'Unknown',
      y: parseFloat(item.revenue || item.totalRevenue || item.amount || 0),
      revenue: parseFloat(item.revenue || item.totalRevenue || item.amount || 0),
      totalSponser:  item.totalSponser || 0,
    }))

    const total = dataPoints.reduce((acc: number, p: any) => acc + p.revenue, 0)

    // The API returns every category even when none earned anything — an
    // all-zero chart is noise, so treat it as no data.
    if (!total) {
      resetChart()
      return
    }

    setChartCategories(categories)
    setChartData([{ name: '', data: dataPoints }])
    setTotalRevenue(String(total))
    setChartLength(String(categories.length))
  }

  const resetChart = () => {
    setChartData([])
    setChartCategories([])
    setTotalRevenue('0')
    setChartLength('0')
  }

  // "All …" and the × both mean the same thing: no value for this param.
  const toSingleValue = (option: Option | null) =>
    option && option.value !== ALL_VALUE ? option : null

  const handleYearChange = (option: Option | null) => {
    const year = toSingleValue(option)
    setDraftYear(year)
    // Month is meaningless without a year — drop it when the year is cleared.
    if (!year) setDraftMonth(null)
  }

  const handleMonthChange = (option: Option | null) => setDraftMonth(toSingleValue(option))

  const handleCategoryChange = (options: Option[] | null) => {
    const picked = Array.isArray(options) ? options : []
    // "All Categories" wins over anything else that was ticked.
    const hasAll = picked.some((option) => option.value === ALL_VALUE)
    setDraftCategories(hasAll ? [] : picked)
  }

  const applyFilters = () => {
    setSelectedYear(draftYear)
    setSelectedMonth(draftMonth)
    setSelectedCategories(draftCategories)
  }

  const clearFilters = () => {
    setDraftYear(null)
    setDraftMonth(null)
    setDraftCategories([])
    setSelectedYear(null)
    setSelectedMonth(null)
    setSelectedCategories([])
  }

  useEffect(() => {
    fetchCategoryOptions()
  }, [fetchCategoryOptions])

  useEffect(() => {
    fetchCategoryRevenue()
  }, [fetchCategoryRevenue])

  useEffect(() => {
    document.title = 'Insayr | Revenue By Category'
  }, [])

  return (
    <>
      <ToastContainer />
      {isLoading && <Loader />}

      {/* Info Banner */}
      <div className='vs-info-banner mb-5'>
        <KTIcon iconName='chart-simple' className='fs-1 text-primary flex-shrink-0' />
        <div>
          <div className='fw-bold fs-6 text-primary mb-1'>{t('Revenue By Category')}</div>
          <p className='text-gray-700 fs-7 mb-0'>
            {t('Revenue breakdown by ad category. Hover over each bar to see the revenue and total sponser for that category.')}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className='card border-0 shadow-sm mb-5'>
        <div className='card-body py-4 px-6'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <div className='d-flex align-items-center gap-2 text-gray-600 fs-7 fw-semibold'>
              <KTIcon iconName='filter' className='fs-4' />
              <span>{t('Filters')}</span>
            </div>

            <div style={{ minWidth: 150 }}>
              <Select
                options={yearOptions}
                value={draftYear}
                onChange={(val: any) => handleYearChange(val)}
                isClearable
                placeholder={t('All Years')}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            <div style={{ minWidth: 170 }}>
              <Select
                options={monthOptions}
                value={draftMonth}
                onChange={(val: any) => handleMonthChange(val)}
                isClearable
                isDisabled={!draftYear}
                placeholder={draftYear ? t('All Months') : t('Select a year first')}
                styles={filterSelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            <div style={{ width: 260 }}>
              <Select
                options={categorySelectOptions}
                value={draftCategories}
                onChange={(val: any) => handleCategoryChange(val)}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ ValueContainer: CategoryValueContainer }}
                isClearable
                isLoading={categoriesLoading}
                placeholder={t('All Categories')}
                styles={categorySelectStyles}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            </div>

            <button
              type='button'
              className='btn btn-primary btn-sm d-flex align-items-center gap-1'
              onClick={applyFilters}
              disabled={!hasPendingFilters}
            >
              <KTIcon iconName='check' className='fs-5' />
              {t('Apply')}
            </button>

            {canClear && (
              <button
                type='button'
                className='btn btn-light-danger btn-sm d-flex align-items-center gap-1'
                onClick={clearFilters}
              >
                <KTIcon iconName='cross-circle' className='fs-5' />
                {t('Clear')}
              </button>
            )}
          </div>
        </div>
      </div>

      <DynamicChart
        className='card-xl-stretch mb-5 mb-xl-8'
        title='Total Revenue'
        totalValue={totalRevenue}
        categories={chartCategories}
        data={chartData}
        tooltipFields={TOOLTIP_FIELDS}
        length={chartLength}
        emptyMessage={emptyMessage}
      />
    </>
  )
}

export { RevenueByCategory }
